import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { confirmPlanChange, previewPlanChange } from '@/lib/subscription-api'
import { getErrorMessage } from '@/lib/error-messages'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useId, useState } from 'react'

export function SubscriptionPlanChange(
  { packageType, onComplete }: { packageType: string; onComplete: () => void },
) {
  const client = useQueryClient()
  const confirmationId = useId()
  const [confirmedPreview, setConfirmedPreview] = useState<string | null>(null)
  const preview = useQuery({
    queryKey: ['plan-change-preview', packageType],
    queryFn: () => previewPlanChange(packageType),
    staleTime: 0,
    retry: false,
  })
  const mutation = useMutation({
    mutationFn: () => confirmPlanChange(packageType, preview.data!, confirmedPreview === preview.data?.previewId),
    onSuccess: (result) => {
      void client.invalidateQueries()
      if (result.paymentUrl) {
        window.location.assign(result.paymentUrl)
        return
      }
      const pendingCancellations = result.pendingBookingCancellations?.length ?? 0
      const consequences = `${result.cancelledBookings.length ? ` ${result.cancelledBookings.length} Buchung(en) wurden storniert.` : ''}${pendingCancellations ? ` ${pendingCancellations} Buchung(en) werden storniert, sobald der neue Tarif aktiv ist.` : ''}${result.replacementRemoved ? ' Die bisherige Vormerkung wird nicht wiederhergestellt.' : ''}`
      const message = result.status === 'scheduled' ? 'Tarifwechsel zum nächsten Abrechnungstermin vorgemerkt.'
        : result.status === 'payment_pending' ? 'Die Zahlung ist noch offen. Dein bisheriger Tarif bleibt bestehen.'
        : result.status === 'failed' ? 'Die Tarifumstellung ist fehlgeschlagen. Dein bisheriger Tarif bleibt bestehen.'
        : result.status === 'applied' ? 'Dein Tarif wurde umgestellt.'
        : 'Der bestätigte Wechsel wird noch verarbeitet. Du musst ihn nicht erneut bestätigen.'
      if (['scheduled', 'applied'].includes(result.status)) toast.success(message + consequences)
      else toast.warning(message + consequences, { duration: 10000 })
      onComplete()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
      if (error.message === 'PLAN_PREVIEW_CHANGED' || error.message === 'PLAN_PREVIEW_EXPIRED') {
        void preview.refetch()
      }
    },
  })
  return (
    <div className='space-y-3 rounded-md border p-3 text-sm'>
      {preview.isPending && <p>Wechsel und Preis werden geprüft …</p>}
      {preview.error && <p className='text-destructive'>{getErrorMessage(preview.error)}</p>}
      {preview.data && (
        <>
          <p>
            {preview.data.changeKind === 'upgrade'
              ? 'Upgrade sofort nach erfolgreicher Zahlung.'
              : preview.data.changeKind === 'same_price'
              ? 'Sofortiger Tarifwechsel ohne anteilige Nachzahlung oder Gutschrift. Dein Abrechnungstermin bleibt unverändert.'
              : `Wechsel ab ${new Date(preview.data.effectiveAt).toLocaleDateString('de-AT')}.`}
          </p>
          {preview.data.changeKind === 'upgrade' && (
            <p>
              Jetzt fälliger Betrag:{' '}
              <strong>
                {new Intl.NumberFormat('de-AT', {
                  style: 'currency',
                  currency: preview.data.currency,
                }).format(preview.data.amountDue / 100)}
              </strong>
            </p>
          )}
          {preview.data.replacement && <p>
            Die Vormerkung für „{preview.data.replacement.packageName}“ ab{' '}
            {new Date(preview.data.replacement.effectiveAt).toLocaleDateString('de-AT')} wird ersetzt.
            Sie wird auch bei einer später fehlgeschlagenen Upgrade-Zahlung nicht wiederhergestellt.
          </p>}
          {preview.data.affectedBookings.length > 0 && (
            <div>
              <p className='font-semibold'>Der neue Tarif deckt diese Buchungen nicht mehr ab. Sie werden storniert, sobald der neue Tarif aktiv ist:</p>
              <ul className='list-disc pl-5'>
                {preview.data.affectedBookings.map((booking) => (
                  <li key={booking.id}>
                    {booking.course_name} · {new Date(booking.starts_at).toLocaleString('de-AT')}
                  </li>
                ))}
              </ul>
              <p className='mt-2'>
                Scheitert die Zahlung oder wird der Wechsel nicht wirksam, bleiben die Buchungen bestehen.
                Guthabenanteile stornierter Buchungen werden zurückerstattet.
              </p>
            </div>
          )}
          {(preview.data.affectedBookings.length > 0 || preview.data.replacement) && (
            <div className='flex items-start gap-2'>
              <Checkbox id={confirmationId}
                checked={confirmedPreview === preview.data.previewId}
                onCheckedChange={(checked) => setConfirmedPreview(checked === true ? preview.data!.previewId : null)} />
              <label htmlFor={confirmationId}>
                Ich möchte den Tarif wirklich wechseln und bestätige
                {preview.data.affectedBookings.length > 0 ? ' die Stornierung der aufgelisteten Buchungen, sobald der neue Tarif aktiv ist' : ''}
                {preview.data.affectedBookings.length > 0 && preview.data.replacement ? ' sowie' : ''}
                {preview.data.replacement ? ' das Ersetzen meiner bisherigen Vormerkung' : ''}.
              </label>
            </div>
          )}
          <p className='text-muted-foreground'>
            Bereits verbrauchte Wochencredits werden nicht zurückgesetzt. Zusätzliches Kaufguthaben wird nicht automatisch eingesetzt.
          </p>
          <Button type='button' disabled={mutation.isPending ||
            Boolean((preview.data.affectedBookings.length > 0 || preview.data.replacement) && confirmedPreview !== preview.data.previewId)} onClick={() => mutation.mutate()}>
            {preview.data.changeKind === 'upgrade'
              ? 'Zahlungspflichtig upgraden'
              : preview.data.changeKind === 'same_price' ? 'Tarif jetzt wechseln'
              : 'Wechsel zum Laufzeitende bestätigen'}
          </Button>
        </>
      )}
    </div>
  )
}
